import { useMemo } from 'react';
import { useEntitlements } from '~/hooks/use-entitlements';
import { PendingSubscriptionPlan, usePendingSubscriptionPlan } from '~/hooks/use-pending-subscription-plan';
import { findPlanCard, usePlanCards } from './use-plan-cards';
import { PlanCard } from './types';
import { ACTIVE_STATUSES } from './use-has-subscription';

export type PendingPlanDecision =
  /** Entitlements have not settled - nothing can be decided yet. */
  | { kind: 'pending' }
  /** Not subscribed, and a plan is waiting: offer the subscribe step. */
  | { kind: 'show'; plan: PendingSubscriptionPlan }
  /**
   * Do not offer the subscribe step. `redirectToMyPlan` is true only when we can POSITIVELY
   * determine the customer asked for a different plan from the one they hold.
   */
  | { kind: 'skip'; redirectToMyPlan: boolean };

const SKIP = { kind: 'skip', redirectToMyPlan: false } as const;

/**
 * Whether the customer asked for a price other than the one they hold.
 *
 * Compared by Stripe price id, through the catalogue. The catalogue is the only thing that knows
 * which price a lookup key or a tier/interval pair actually names, so asking it is both exact and
 * free of any mirrored list of key names this app would have to keep in step by hand.
 *
 * Deliberately false whenever either side cannot be identified - a catalogue that has not loaded, a
 * key it does not carry, a subscription on a retired price. A redirect on a guess sends someone to
 * a checkout for a plan they may already hold, and an already-held plan is the case the embedded
 * frame handles worst: it answers `409 same_price`, posts nothing, and sits silent.
 */
function wantsSomethingElse(plan: PendingSubscriptionPlan, heldPriceId: string | null, cards: PlanCard[]): boolean {
  if (!heldPriceId) {
    return false;
  }
  const wanted = findPlanCard(cards, plan);
  return !!wanted && wanted.priceId !== heldPriceId;
}

/**
 * What onboarding should do about a plan the customer picked before they got here.
 *
 * One hook rather than the rule restated at each call site: the screen that decides whether to show
 * the subscribe step and the completion that decides where to send them afterwards have to agree, or
 * a customer is either shown a step that cannot succeed or quietly loses the plan they chose.
 *
 * **A subscriber never sees the step**, whatever they picked. The step exists to sell a plan, and
 * the single-plan frame has no concept of "you already have this" - pointed at a plan they hold it
 * answers `409 same_price`, posts no completion message and strands the wizard behind it.
 *
 * A failed entitlements read also skips, without a redirect: a read that failed is not evidence the
 * customer has no plan, and showing the step on that basis risks exactly the dead end above. Nothing
 * is lost by skipping - My Plan lists every plan, so they can still choose one there.
 */
export function usePendingPlanDecision(): PendingPlanDecision {
  const plan = usePendingSubscriptionPlan();
  const { subscription, status, billingDetailUnavailable } = useEntitlements();
  // Shared, tenant-scoped and deduped by SWR, so consulting it here costs no extra request on a page
  // that already shows plans. A catalogue that fails to load only costs the redirect, never the skip.
  // Only read with a plan to compare: there is none for a tenant that sells no subscriptions, and
  // this hook is mounted on every onboarding visit.
  const { cards } = usePlanCards({ enabled: !!plan });

  return useMemo(() => {
    if (!plan) {
      return SKIP;
    }
    if (status === 'loading') {
      return { kind: 'pending' };
    }
    // A read that failed, or perks granted with no billing detail behind them, both mean "we cannot
    // say what they hold" - not "they hold nothing".
    if (status === 'error' || billingDetailUnavailable) {
      return SKIP;
    }
    if (subscription && ACTIVE_STATUSES.has(subscription.status)) {
      return { kind: 'skip', redirectToMyPlan: wantsSomethingElse(plan, subscription.priceId ?? null, cards) };
    }
    return { kind: 'show', plan };
  }, [plan, subscription, status, billingDetailUnavailable, cards]);
}
